/**
 * The studio seen as dancers rather than as families (D-151).
 *
 * Households are how the studio bills; almost nothing Tonya asks day to day is
 * a question about a household. "How long has she danced here?", "what size is
 * she?", "which classes is she in?" are all questions about a child.
 */

export type DancerRow = {
  id: string
  firstName: string
  lastName: string
  dateOfBirth: string
  age: number
  status: string
  startedOn: string | null
  /** Complete years with the studio; null when Jackrabbit holds no start date. */
  years: number | null
  /** 5, 10, 15… when this dancer is at an award year. */
  milestone: number | null
  householdId: string
  householdName: string
  classes: number
  hasMeasurements: boolean
  /** In at least one competitive class, so the 80% rule applies to her. */
  isComp: boolean
}

/** One selectable teacher. Filtered on by id, never by name (D-82). */
export type DancerTeacherOption = {
  staffId: string
  name: string
  dancers: number
}

export type DancerRoster = {
  dancers: DancerRow[]
  total: number
  milestones: number
  /** Dancers with no start date, so no tenure can be computed. */
  tenureUnknown: number
  teachers: DancerTeacherOption[]
}

export type DancerClassRow = {
  classId: string
  name: string
  day: string | null
  time: string | null
  room: string | null
  teachers: string | null
  status: string
}

/**
 * Costume sizes and measurements — one editable set (D-198).
 *
 * The studio owns these. An import fills a field nobody has entered and never
 * overwrites one somebody has, so nothing typed here is at risk from the next
 * upload. `bust` and `hip` are the studio's words for the stored chest and
 * hips.
 */
export type DancerMeasurements = {
  tShirt: string | null
  leotard: string | null
  pant: string | null
  shoe: string | null
  tights: string | null
  height: string | null
  bust: string | null
  waist: string | null
  hip: string | null
  girth: string | null
  inseam: string | null
  notes: string | null
  /** When they were taken, so an old figure reads as old. */
  measuredOn: string | null
}

export type DancerGuardian = {
  name: string
  email: string | null
  phone: string | null
  relationship: string | null
}

export type DancerRecord = {
  id: string
  firstName: string
  lastName: string
  dateOfBirth: string
  age: number
  status: string
  startedOn: string | null
  years: number | null
  milestone: number | null
  nextMilestone: number | null
  yearsToNextMilestone: number | null
  withdrawnOn: string | null
  notes: string | null
  householdId: string
  householdName: string
  guardians: DancerGuardian[]
  classes: DancerClassRow[]
  measurements: DancerMeasurements
  /**
   * Whether *this reader* may write the measurements (D-397).
   *
   * Answered by the server, because a teacher may record sizes for a dancer
   * she teaches and this record carries teacher names rather than ids (D-82) —
   * so nothing here could work it out. Reading them was always open;
   * `ViewPeople` is what gets you the record at all.
   */
  canEditMeasurements: boolean

  /** What the staff list calls her, when she is also on it (D-535). */
  staffRecordName: string | null

  staffId: string | null

  /**
   * She is on the company team, and held to its attendance (D-652).
   *
   * Optional, and read with `?? false`, so a record from a host that predates
   * the field leaves the flag off rather than guessing.
   */
  isCompanyMember?: boolean
  /** Her privates come out of credit on the account rather than out of pocket (D-662). */
  holdsAccountCredit?: boolean

  /** Whether *this reader* may change that — answered by the server (D-652). */
  canSetCompanyMember?: boolean

  /**
   * The named sub-teams she is on — the Mini team (D-841).
   *
   * **The same `canSetCompanyMember` says whether this reader may change these**: it
   * is the one `ManagePeople` door, and a second flag saying the same thing would be
   * two copies of one idea.
   *
   * Optional and read with `?? []`, so a record from a host that predates the field
   * shows no teams rather than guessing.
   */
  teams?: string[]
}

export type DancerSort = 'name' | 'age' | 'tenure' | 'classes'

export type DancerFilter = {
  q?: string
  /** "active" (default), "former", or "all". */
  status?: string
  /** Only dancers at exactly this tenure. */
  milestone?: number
  /** Only dancers at any award year. */
  milestonesOnly?: boolean
  /** "comp" or "rec" — asked of her enrolments, not of her (D-183). */
  program?: 'comp' | 'rec'
  /** Staff id. Never a name: the studio has two Ashleys (D-82). */
  teacher?: string
  sort?: DancerSort
}

export async function fetchDancers(filter: DancerFilter = {}): Promise<DancerRoster> {
  const params = new URLSearchParams()
  if (filter.q?.trim()) params.set('q', filter.q.trim())
  if (filter.status) params.set('status', filter.status)
  if (filter.milestone) params.set('milestone', String(filter.milestone))
  if (filter.milestonesOnly) params.set('milestonesOnly', 'true')
  if (filter.program) params.set('program', filter.program)
  if (filter.teacher) params.set('teacher', filter.teacher)
  if (filter.sort && filter.sort !== 'name') params.set('sort', filter.sort)

  const res = await fetch(`/api/admin/dancers/?${params}`)
  if (!res.ok) throw new Error(`Could not load the dancers (${res.status})`)
  return res.json()
}

/**
 * What is currently narrowing the list, in words.
 *
 * <b>Six controls can hide 700 dancers between them.</b> With a stale filter
 * set, "Nobody matches" is indistinguishable from an empty studio — so the
 * screen has to be able to say which filters are on, and offer to drop them.
 */
export function activeFilters(
  filter: DancerFilter,
  teachers: DancerTeacherOption[],
): string[] {
  const active: string[] = []

  if (filter.q?.trim()) active.push(`named "${filter.q.trim()}"`)
  if (filter.status === 'former') active.push('former dancers')
  if (filter.status === 'all') active.push('current and former')
  if (filter.program === 'comp') active.push('competitive')
  if (filter.program === 'rec') active.push('recreational')

  if (filter.teacher) {
    // Falls back to the id rather than dropping the chip: a teacher who has
    // left mid-session still explains why the list is empty.
    const teacher = teachers.find((t) => t.staffId === filter.teacher)
    active.push(teacher ? `taught by ${teacher.name}` : 'one teacher')
  }

  if (filter.milestone) active.push(`at ${filter.milestone} years`)
  else if (filter.milestonesOnly) active.push('at an award year')

  return active
}

export async function fetchDancer(id: string): Promise<DancerRecord> {
  const res = await fetch(`/api/admin/dancers/${id}`)
  if (!res.ok) throw new Error(`Could not load that dancer (${res.status})`)
  return res.json()
}

/**
 * She holds credit on the account, so a private is charged against it (D-662).
 *
 * A flag and never a balance: the app does not track the money, Tonya does the
 * charging in Jackrabbit (D-54). Same capability and same idempotence as the
 * company switch below it.
 */
export async function setAccountCredit(
  id: string,
  holdsAccountCredit: boolean,
): Promise<{ id: string; holdsAccountCredit: boolean }> {
  const res = await fetch(`/api/admin/dancers/${id}/account-credit`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ holdsAccountCredit }),
  })

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
  return res.json()
}

/**
 * Puts her on the company team, or takes her off it (D-652).
 *
 * Idempotent: a second tap writes no audit row, because the endpoint compares
 * the value before and after. The domain setter returns early too, so nothing
 * is touched at all.
 */
export async function setCompanyMember(
  id: string,
  isCompanyMember: boolean,
): Promise<{ id: string; isCompanyMember: boolean }> {
  const res = await fetch(`/api/admin/dancers/${id}/company`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isCompanyMember }),
  })

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
  return res.json()
}

/**
 * The name of the studio's sub-team, and the only one offered today (D-840).
 *
 * **The table holds any number and the screen offers one**, deliberately: a second
 * team needs no migration, and until somebody asks for one, a picker would be a
 * control with a single option. This constant is the one place that knows there is
 * a Mini team, so the day there are two, this is what changes.
 */
export const MINI_TEAM = 'Mini team'

/**
 * Puts her on a named sub-team, or takes her off it (D-841).
 *
 * **Keyed on the name rather than an id**, because this switch knows the studio has
 * a Mini team and does not know its key — and the team is created the first time it
 * is used, since there is no screen for making one.
 *
 * Idempotent like the company flag: a second tap writes no audit row.
 */
export async function setDancerTeam(
  id: string,
  team: string,
  isMember: boolean,
): Promise<{ id: string; team: string; isMember: boolean }> {
  const res = await fetch(`/api/admin/dancers/${id}/team`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ team, isMember }),
  })

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
  return res.json()
}

export async function saveMeasurements(
  id: string,
  edit: DancerMeasurements,
): Promise<DancerMeasurements> {
  const res = await fetch(`/api/admin/dancers/${id}/measurements`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(edit),
  })

  if (!res.ok) {
    // The server writes the useful sentence; repeating it here in different
    // words would give one condition two names.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? `Could not save those measurements (${res.status})`)
  }

  return res.json()
}

export type MeasurementKey = Exclude<keyof DancerMeasurements, 'notes' | 'measuredOn'>

/**
 * Every measurement, labelled and in the order a costume order reads.
 *
 * Body measurements first, then garment sizes: the body ones are what she takes
 * with a tape at the start of a season, and the sizes follow from them.
 */
export function measurementFields(
  m: DancerMeasurements,
): { key: MeasurementKey; label: string; value: string | null; group: 'body' | 'size' }[] {
  return [
    { key: 'bust', label: 'Bust', value: m.bust, group: 'body' },
    { key: 'waist', label: 'Waist', value: m.waist, group: 'body' },
    { key: 'hip', label: 'Hip', value: m.hip, group: 'body' },
    { key: 'girth', label: 'Girth', value: m.girth, group: 'body' },
    { key: 'height', label: 'Height', value: m.height, group: 'body' },
    { key: 'inseam', label: 'Inseam', value: m.inseam, group: 'body' },

    { key: 'leotard', label: 'Leotard', value: m.leotard, group: 'size' },
    { key: 'tights', label: 'Tights', value: m.tights, group: 'size' },
    { key: 'shoe', label: 'Shoes', value: m.shoe, group: 'size' },
    { key: 'tShirt', label: 'T-shirt', value: m.tShirt, group: 'size' },
    { key: 'pant', label: 'Pants', value: m.pant, group: 'size' },
  ]
}

/** Whether anything at all has been recorded for this dancer. */
export function hasMeasurements(m: DancerMeasurements): boolean {
  return measurementFields(m).some((f) => f.value) || Boolean(m.notes)
}

/**
 * How old these measurements are, in words.
 *
 * <b>A measurement with no age reads as current however old it is.</b> A
 * nine-year-old's girth moves a great deal in a season, and ordering from last
 * year's is the mistake the date exists to prevent — so the screen says how
 * stale it is rather than printing a date and leaving the arithmetic to
 * whoever is reading.
 */
export function measurementAge(measuredOn: string | null, today: Date = new Date()): string | null {
  if (!measuredOn) return null

  // Parsed as local midnight rather than through Date(string), which reads a
  // bare yyyy-mm-dd as UTC and lands on the previous day west of Greenwich.
  const [y, m, d] = measuredOn.split('-').map(Number)
  const taken = new Date(y, m - 1, d)

  const days = Math.floor((today.getTime() - taken.getTime()) / 86_400_000)

  if (days < 0) return 'Dated in the future'
  if (days === 0) return 'Measured today'
  if (days === 1) return 'Measured yesterday'
  if (days < 30) return `Measured ${days} days ago`

  const months = Math.floor(days / 30)
  if (months < 12) return `Measured ${months} month${months === 1 ? '' : 's'} ago`

  const years = Math.floor(days / 365)
  return `Measured over ${years} year${years === 1 ? '' : 's'} ago`
}

/**
 * How a dancer's tenure should read.
 *
 * Never "0 years" for an unknown start date: around five dancers have none in
 * Jackrabbit, and printing zero would read as a fact about how long they have
 * danced rather than an absence of one — which is how a long-serving dancer
 * gets missed at a recital.
 */
export function tenureLabel(years: number | null): string {
  if (years === null) return 'Not recorded'
  if (years === 0) return 'First season'
  return years === 1 ? '1 year' : `${years} years`
}

/**
 * Only the measurements that have a value, for reading rather than editing.
 *
 * Thirteen labelled blanks read as a data problem; the truthful answer is that
 * nobody has measured this dancer yet, and the card says so instead.
 */
export function recordedMeasurements(m: DancerMeasurements): { label: string; value: string }[] {
  return measurementFields(m)
    .filter((f): f is typeof f & { value: string } => Boolean(f.value))
    .map((f) => ({ label: f.label, value: f.value }))
}
